import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiArrowLeft, HiCheck } from "react-icons/hi";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import "./EditInvoice.scss";

export const EditInvoice: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['invoices', 'common']);
  const { token } = useAuth();

  const [formData, setFormData] = useState({
    id: id,
    receivedInvoiceNumber: "",
    externalInvoiceNumber: "",
    paymentGatewayId: "" as string | number,
  });

  const [paymentGateways, setPaymentGateways] = useState<{id: number, name: string, localizedName?: string | null}[]>([]);

  useEffect(() => {
    const fetchGateways = async () => {
      try {
        const response = await adminApi.call('/paymentgateway/active', 'GET');
        if (response.success && response.data) {
          setPaymentGateways(response.data);
        }
      } catch (error) {
        console.error("Error fetching payment gateways", error);
      }
    };
    fetchGateways();
  }, [token, i18n.language]);

  const [loading, setLoading] = useState(false);

  // Fetch invoice by ID
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const response = await adminApi.call(`/invoice/${id}`, 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('invoices:messages.fetchError'));
          return;
        }

        const data = response.data;
        setFormData({
          id: data.id,
          receivedInvoiceNumber: data.receivedInvoiceNumber || "",
          externalInvoiceNumber: data.externalInvoiceNumber || "",
          paymentGatewayId: data.paymentGatewayId || "",
        });
      } catch (error: any) {
        showErrorToast(t('invoices:messages.fetchError') + ": " + error.message);
      }
    };

    fetchInvoice();
  }, [id, token, i18n.language]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = e.currentTarget;
    const { name, value } = target;
    const isCheckedInput = target instanceof HTMLInputElement && target.type === "checkbox";
    setFormData((prev) => ({
      ...prev,
      [name]: isCheckedInput ? target.checked : value,
    }));
  };

  const submitUpdate = async () => {
    setLoading(true);
    const payload = {
      ...formData,
      paymentGatewayId: formData.paymentGatewayId === "" ? null : Number(formData.paymentGatewayId),
    };
    try {
      const response = await adminApi.call('/invoice', 'PUT', payload);

      if (!response.success) {
        showErrorToast(response.error || t('invoices:messages.updateError'));
        return;
      }

      showSuccessToast(t('invoices:messages.updateSuccess'));
    } catch (error: any) {
      showErrorToast(t('invoices:messages.updateError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t('invoices:breadcrumbs.invoices'), path: '/invoices' },
    { label: t('invoices:breadcrumbs.edit') }
  ];

  return (
    <PageLayout
      title={t('invoices:editInvoice')}
      subtitle={t('invoices:subtitle')}
      breadcrumbs={breadcrumbs}
    >
      <div className="modern-form">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">{t('invoices:fields.paymentGateway') || 'Payment Gateway'}</label>
            <select
              className="form-control"
              name="paymentGatewayId"
              value={formData.paymentGatewayId}
              onChange={handleInputChange}
            >
              <option value="">-- {t('common:select') || 'Select'} --</option>
              {paymentGateways.map(pg => (
                <option key={pg.id} value={pg.id}>{pg.localizedName || pg.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">{t('invoices:fields.externalInvoiceNumber') || 'External Invoice Number'}</label>
            <input
              type="text"
              className="form-control"
              name="externalInvoiceNumber"
              value={formData.externalInvoiceNumber}
              onChange={handleInputChange}
              placeholder={t('invoices:placeholders.externalInvoiceNumber')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('invoices:fields.receivedInvoiceNumber')}</label>
            <input
              type="text"
              className="form-control"
              name="receivedInvoiceNumber"
              value={formData.receivedInvoiceNumber}
              onChange={handleInputChange}
              placeholder={t('invoices:placeholders.receivedInvoiceNumber')}
            />
          </div>
        </div>

        <div className="form-actions">
          <ActionButton
            variant="primary"
            icon={HiCheck}
            onClick={submitUpdate}
            loading={loading}
            disabled={loading}
          >
            {loading ? t('invoices:buttons.updating') : t('invoices:buttons.update')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
